import { useCallback, useEffect, useState } from "react";
import { useData } from "@/context/DataContext";
import { AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { Empty } from "@/components/Common";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DevicesByCustomer } from "@/components/warehouse/DevicesByCustomer";

export const STOCK_STATUS = { NEGATIVE: ["Negativa", "bg-red-100 text-red-800"], LOW: ["Sotto scorta", "bg-amber-100 text-amber-900"], OK: ["OK", "bg-emerald-50 text-emerald-800"] };

export const WarehouseSelect = ({ value, onChange, warehouses, all, testId, className = "w-64 bg-white" }) => (
  <Select value={value || (all ? "all" : undefined)} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
    <SelectTrigger className={className} data-testid={testId}><SelectValue placeholder="Seleziona magazzino" /></SelectTrigger>
    <SelectContent>
      {all && <SelectItem value="all">{all}</SelectItem>}
      {warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.kind === "VAN" ? "Furgone · " : ""}{w.name.replace(/^Furgone /, "")}</SelectItem>)}
    </SelectContent>
  </Select>
);

export const StockTab = ({ warehouses, refreshKey, onOpenSerials }) => {
  const [view, setView] = useState("items");
  const [wh, setWh] = useState("");
  const [q, setQ] = useState("");
  const [onlyAlerts, setOnlyAlerts] = useState(false);
  const { companyId } = useData();
  const [raw, setRows] = useState([]);
  const ids = new Set(warehouses.map((w) => w.id));
  const rows = companyId ? raw.filter((r) => ids.has(r.warehouse_id)) : raw;
  useEffect(() => { setWh(""); }, [companyId]);
  const load = useCallback(() => api.get("/warehouse/stock", { params: { warehouse_id: wh || undefined } }).then((r) => setRows(r.data)).catch(() => {}), [wh]);
  useEffect(() => { load(); }, [load, refreshKey]);
  const alerts = rows.filter((r) => r.status !== "OK");
  const shown = rows.filter((r) => (!onlyAlerts || r.status !== "OK") && (!q || `${r.code} ${r.name}`.toLowerCase().includes(q.toLowerCase())));
  return (
    <div data-testid="stock-tab">
      {alerts.length > 0 && (
        <button onClick={() => setOnlyAlerts(!onlyAlerts)} className="mb-4 flex w-full items-center gap-3 border-l-4 border-red-600 bg-red-50 px-4 py-3 text-left text-sm" data-testid="stock-alerts-banner">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <span><strong>{alerts.filter((a) => a.status === "NEGATIVE").length}</strong> giacenze negative e <strong>{alerts.filter((a) => a.status === "LOW").length}</strong> sotto scorta minima</span>
          <span className="ml-auto text-xs font-semibold underline">{onlyAlerts ? "Mostra tutto" : "Mostra solo avvisi"}</span>
        </button>
      )}
      <div className="mb-3 flex flex-wrap gap-3">
        <div className="inline-flex border bg-white p-1" data-testid="stock-view">
          {[["items", "Articoli"], ["devices", "Apparati per committente"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setView(k)} className={`px-3 py-1.5 text-sm font-semibold transition-colors ${view === k ? "bg-foreground text-background" : "hover:bg-muted"}`} data-testid={`stock-view-${k}`}>{l}</button>
          ))}
        </div>
        <WarehouseSelect value={wh} onChange={setWh} warehouses={warehouses} all="Tutti i magazzini" testId="stock-warehouse-filter" />
        {view === "items" && <Input className="w-64 bg-white" placeholder="Cerca codice o descrizione" value={q} onChange={(e) => setQ(e.target.value)} data-testid="stock-search" />}
      </div>
      {view === "devices" ? <DevicesByCustomer warehouses={warehouses} wh={wh} refreshKey={refreshKey} onOpenSerials={onOpenSerials} /> : shown.length === 0 ? <Empty text="Nessuna giacenza" testId="stock-empty" /> : (
        <div className="overflow-x-auto border bg-white">
          <Table>
            <TableHeader><TableRow className="bg-muted/50"><TableHead>Magazzino</TableHead><TableHead>Codice</TableHead><TableHead>Articolo</TableHead><TableHead className="text-right">Giacenza</TableHead><TableHead className="text-right">Scorta min.</TableHead><TableHead>Stato</TableHead></TableRow></TableHeader>
            <TableBody>
              {shown.map((r) => {
                const [label, cls] = STOCK_STATUS[r.status];
                return (
                  <TableRow key={`${r.warehouse_id}${r.item_id}`} data-testid={`stock-row-${r.warehouse_id}-${r.code}`}>
                    <TableCell className="text-sm">{r.warehouse_name}</TableCell>
                    <TableCell className="font-mono text-xs">{r.code}</TableCell>
                    <TableCell className="text-sm">{r.name}{r.serialized && <span className="ml-2 text-[10px] font-semibold uppercase text-muted-foreground">a seriale</span>}</TableCell>
                    <TableCell className={`text-right font-mono font-semibold ${r.qty < 0 ? "text-red-700" : ""}`}>{r.qty} {r.unit}</TableCell>
                    <TableCell className="text-right font-mono text-xs text-muted-foreground">{r.min_stock || "—"}</TableCell>
                    <TableCell><span className={`px-1.5 py-0.5 text-[11px] font-semibold uppercase ${cls}`}>{label}</span></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};
